import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CaseId, SourceId } from '@casetrace/shared';
import { createDocumentUpload, uploadDocumentFile, startImport } from '../../lib/callables';
import { useDocumentDrop } from './use-document-drop';
import { SourceTypeIcon } from './source-type-icon';

type UploadState =
  | { phase: 'idle' }
  | { phase: 'uploading'; uploaded: number; total: number }
  | { phase: 'starting' };

async function uploadFilesWithConcurrency(
  files: File[],
  caseId: CaseId,
  sourceId: SourceId,
  onProgress: (uploaded: number) => void,
  concurrency = 3,
): Promise<void> {
  let uploaded = 0;
  const queue = [...files];

  async function runWorker(): Promise<void> {
    while (queue.length > 0) {
      const file = queue.shift();
      if (!file) break;
      await uploadDocumentFile({ caseId, sourceId, file });
      uploaded += 1;
      onProgress(uploaded);
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, runWorker));
}

function statusLabel(state: UploadState): string | null {
  if (state.phase === 'uploading') return `Laster opp ${state.uploaded} av ${state.total}...`;
  if (state.phase === 'starting') return 'Starter import...';
  return null;
}

export function DocumentUploadButton({ caseId }: { readonly caseId: CaseId }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({ phase: 'idle' });

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      setUploadState({ phase: 'uploading', uploaded: 0, total: files.length });
      const { sourceId } = await createDocumentUpload({ caseId, navn: 'Dokumenter' });
      await uploadFilesWithConcurrency(files, caseId, sourceId, (uploaded) => {
        setUploadState({ phase: 'uploading', uploaded, total: files.length });
      });
      setUploadState({ phase: 'starting' });
      return startImport({ caseId, sourceId });
    },
    onSettled: () => setUploadState({ phase: 'idle' }),
  });

  const handleFiles = (files: File[]) => {
    if (files.length > 0) uploadMutation.mutate(files);
  };

  const { isDragOver, dragHandlers } = useDocumentDrop(handleFiles);

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    handleFiles(files);
    event.currentTarget.value = '';
  };

  return (
    <div
      className={`mt-2 rounded-md border transition-colors duration-150 ${
        isDragOver ? 'border-white/30 bg-white/[0.06]' : 'border-white/[0.08] bg-transparent'
      }`}
      {...dragHandlers}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelected}
      />
      <button
        className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2
          text-sm font-medium transition-colors duration-150
          border border-white/[0.12] bg-transparent text-[#ededf0] hover:bg-white/[0.05]
          disabled:pointer-events-none disabled:opacity-40"
        disabled={uploadMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
        type="button"
      >
        <SourceTypeIcon type="document_upload" />
        {isDragOver ? 'Slipp filer her' : 'Last opp dokumenter'}
      </button>
      {isDragOver && (
        <p className="px-3 pb-2 text-center text-xs text-[#5e6070]">Slipp for å laste opp</p>
      )}
      <p className="px-3 pb-2 text-xs text-[#5e6070]">
        Støtter enkeltfiler. ZIP-filer pakkes ut automatisk. Mapper lastes ikke opp direkte, pakk
        mappen som ZIP for import.
      </p>
      {statusLabel(uploadState) && (
        <p className="mt-1 px-3 pb-2 text-xs text-[#5e6070]">{statusLabel(uploadState)}</p>
      )}
      {uploadMutation.error && (
        <p className="mt-1 px-3 pb-2 text-xs text-danger/80">Opplasting feilet. Prøv igjen.</p>
      )}
    </div>
  );
}
